import { useEffect, useMemo, useState } from "react";
import { Finance, Fleet, Employees as EmployeesApi } from "../lib/api";
import {
  TrendingUp, TrendingDown, Wallet, Calendar, Plus, Trash2, Filter, X,
  Receipt, BarChart3, Truck, User, ArrowUpRight, Upload, Pencil,
} from "lucide-react";
import { toast } from "sonner";
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip,
  CartesianGrid, Legend, ReferenceLine,
} from "recharts";
import { useConfirm } from "../components/ConfirmProvider";
import FinanceImportModal from "../components/FinanceImportModal";
import SalaryForm from "../components/SalaryForm";

const EMPTY_TX = {
  date: new Date().toISOString().slice(0, 10),
  due_date: new Date().toISOString().slice(0, 10),
  type: "expense",
  category: "",
  net_amount: "",
  vat_rate: 23,
  amount: "",
  description: "",
  status: "actual",
  vehicle_id: "",
  employee_id: "",
  _lastEdited: "net",
  recurring: false,
  recurring_months: [], // list of "YYYY-MM" strings for months to also create tx for
};

const PL_MONTHS = ["Sty", "Lut", "Mar", "Kwi", "Maj", "Cze", "Lip", "Sie", "Wrz", "Paź", "Lis", "Gru"];

const VAT_RATES = [
  { value: 0, label: "zw. / 0%" },
  { value: 5, label: "5%" },
  { value: 8, label: "8%" },
  { value: 23, label: "23%" },
];

// Helpers — return numeric or 0
const num = (v) => {
  const n = parseFloat(String(v).replace(",", "."));
  return Number.isFinite(n) ? n : 0;
};
const round2 = (n) => Math.round(n * 100) / 100;

const MONTH_LABELS_PL = ["Sty","Lut","Mar","Kwi","Maj","Cze","Lip","Sie","Wrz","Paź","Lis","Gru"];

function fmtMonth(key) {
  const [y, m] = key.split("-");
  return `${MONTH_LABELS_PL[Number(m) - 1]} ${y.slice(2)}`;
}

function fmtMoney(n) {
  if (n == null || isNaN(n)) return "—";
  return new Intl.NumberFormat("pl-PL", { maximumFractionDigits: 0 }).format(n) + " zł";
}

export default function FinancePage() {
  const { confirm } = useConfirm();
  const [tab, setTab] = useState("cashflow");
  const [summary, setSummary] = useState(null);
  const [kup, setKup] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [meta, setMeta] = useState({
    income_categories: [], expense_categories: [],
    categories_with_vehicle: [], categories_with_employee: [],
  });
  const [fleet, setFleet] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState(EMPTY_TX);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [showSalaryForm, setShowSalaryForm] = useState(false);

  const load = async () => {
    const [s, txs, m, f, e, k] = await Promise.all([
      Finance.summary(),
      Finance.list(),
      Finance.meta(),
      Fleet.list(),
      EmployeesApi.list(),
      Finance.kup(),
    ]);
    setSummary(s);
    setTransactions(txs);
    setMeta(m);
    setFleet(f);
    setEmployees(e);
    setKup(k);
  };

  useEffect(() => {
    load();
  }, []);

  const vehicleMap = useMemo(
    () => Object.fromEntries(fleet.map((v) => [v.id, `${v.brand} ${v.model} · ${v.registration}`])),
    [fleet]
  );
  const employeeMap = useMemo(
    () => Object.fromEntries(employees.map((e) => [e.id, `${e.first_name} ${e.last_name}`])),
    [employees]
  );

  const needsVehicle = meta.categories_with_vehicle.includes(form.category);
  const needsEmployee = meta.categories_with_employee.includes(form.category);

  const openForm = (preset) => {
    setForm({ ...EMPTY_TX, ...preset });
    setEditingId(null);
    setShowForm(true);
  };

  const openEditForm = (tx) => {
    // Pre-fill the form from an existing transaction. The gross ↔ net ↔ VAT
    // relationship is preserved and re-editable using the same handlers.
    const gross = Number(tx.amount) || 0;
    const net = Number(tx.net_amount) || gross;
    const vat = tx.vat_rate == null ? 23 : Number(tx.vat_rate);
    setForm({
      date: tx.date || new Date().toISOString().slice(0, 10),
      due_date: tx.due_date || tx.date || "",
      type: tx.type,
      category: tx.category || "",
      net_amount: net ? String(net) : "",
      vat_rate: vat,
      amount: gross ? String(gross) : "",
      description: tx.description || "",
      status: tx.status || "actual",
      vehicle_id: tx.vehicle_id || "",
      employee_id: tx.employee_id || "",
      _lastEdited: "gross",
    });
    setEditingId(tx.id);
    setShowForm(true);
  };

  const submitForm = async (e) => {
    e.preventDefault();
    if (!form.category) {
      toast.error("Wybierz kategorię");
      return;
    }
    const gross = num(form.amount);
    if (gross <= 0) {
      toast.error("Podaj kwotę większą od zera");
      return;
    }
    const net = num(form.net_amount) || gross;
    const vat = num(form.vat_rate);
    const vatAmount = round2(gross - net);
    const payload = {
      date: form.date,
      due_date: form.due_date || form.date,
      type: form.type,
      category: form.category,
      amount: round2(gross),
      net_amount: round2(net),
      vat_rate: vat,
      vat_amount: vatAmount,
      description: form.description,
      status: form.status,
      vehicle_id: needsVehicle && form.vehicle_id ? form.vehicle_id : null,
      employee_id: needsEmployee && form.employee_id ? form.employee_id : null,
    };
    try {
      if (editingId) {
        await Finance.update(editingId, payload);
        toast.success("Zaktualizowano");
      } else {
        // Primary transaction
        await Finance.create(payload);
        // Recurring copies — create one tx per selected future month with the
        // same day-of-month. Skip the primary month to avoid duplicating it.
        let extraCreated = 0;
        if (form.recurring && form.recurring_months?.length && form.type === "expense") {
          const baseDay = form.date.slice(8, 10);
          const baseYm = form.date.slice(0, 7);
          const dueDelta = form.due_date && form.due_date !== form.date
            ? Math.round((new Date(form.due_date) - new Date(form.date)) / 86400000)
            : 0;
          for (const ym of form.recurring_months) {
            if (ym === baseYm) continue;
            // Cap day at month length (Feb 29→28, Apr 31→30)
            const [y, m] = ym.split("-").map(Number);
            const lastDay = new Date(y, m, 0).getDate();
            const day = Math.min(parseInt(baseDay, 10), lastDay);
            const date = `${ym}-${String(day).padStart(2, "0")}`;
            const dueDate = dueDelta
              ? new Date(new Date(date).getTime() + dueDelta * 86400000).toISOString().slice(0, 10)
              : date;
            await Finance.create({
              ...payload,
              date,
              due_date: dueDate,
              description: (payload.description || form.category) + ` [cykliczny ${ym}]`,
            });
            extraCreated += 1;
          }
        }
        if (extraCreated > 0) {
          toast.success(`Dodano ${extraCreated + 1} wpisów cyklicznych`);
        } else {
          toast.success(form.type === "income" ? "Przychód dodany" : "Koszt dodany");
        }
      }
      setForm({ ...EMPTY_TX });
      setEditingId(null);
      setShowForm(false);
      load();
    } catch (err) {
      toast.error("Błąd: " + (err.response?.data?.detail || err.message));
    }
  };

  const removeTx = async (id) => {
    if (!(await confirm("Usunąć tę transakcję?", { tone: "danger", confirmLabel: "Usuń" }))) return;
    try {
      await Finance.remove(id);
      toast.success("Usunięto");
      load();
    } catch (err) {
      toast.error("Błąd: " + (err.response?.data?.detail || err.message));
    }
  };

  return (
    <div className="space-y-8" data-testid="finance-page">
      <div>
        <div className="font-label mb-1">// Analiza finansowa</div>
        <h1 className="font-heading font-black text-4xl uppercase tracking-tight">
          Finanse
        </h1>
        <p className="text-zinc-600 mt-2 text-sm">
          Cashflow, koszty uzyskania przychodu (KUP) oraz ewidencja przychodów.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex border border-zinc-900 w-fit">
        <TabButton id="cashflow" activeId={tab} setTab={setTab} icon={BarChart3} label="Cashflow" testId="tab-cashflow" />
        <TabButton id="kup" activeId={tab} setTab={setTab} icon={Receipt} label="KUP" testId="tab-kup" brand />
        <TabButton id="income" activeId={tab} setTab={setTab} icon={TrendingUp} label="Przychód" testId="tab-income" emerald />
        <TabButton id="salaries" activeId={tab} setTab={setTab} icon={User} label="Wynagrodzenia" testId="tab-salaries" />
      </div>

      {tab === "cashflow" && (
        <CashflowTab summary={summary} transactions={transactions} vehicleMap={vehicleMap} employeeMap={employeeMap} />
      )}

      {tab === "kup" && (
        <KupTab
          kup={kup}
          transactions={transactions.filter((t) => t.type === "expense")}
          vehicleMap={vehicleMap}
          employeeMap={employeeMap}
          onAdd={() => openForm({ type: "expense" })}
          onImport={() => setShowImport(true)}
          onRemove={removeTx}
          onEdit={openEditForm}
        />
      )}

      {tab === "income" && (
        <IncomeTab
          summary={summary}
          transactions={transactions.filter((t) => t.type === "income")}
          onAdd={() => openForm({ type: "income" })}
          onImport={() => setShowImport(true)}
          onRemove={removeTx}
          onEdit={openEditForm}
        />
      )}

      {tab === "salaries" && (
        <SalariesTab
          transactions={transactions.filter((t) => t.type === "expense" && t.category === "Wynagrodzenia")}
          employees={employees}
          employeeMap={employeeMap}
          onAdd={() => setShowSalaryForm(true)}
          onRemove={removeTx}
        />
      )}

      {showForm && (
        <TransactionForm
          form={form}
          setForm={setForm}
          submitForm={submitForm}
          close={() => { setShowForm(false); setEditingId(null); }}
          meta={meta}
          fleet={fleet}
          employees={employees}
          needsVehicle={needsVehicle}
          needsEmployee={needsEmployee}
          editing={!!editingId}
        />
      )}

      <FinanceImportModal
        open={showImport}
        onClose={() => setShowImport(false)}
        meta={meta}
        fleet={fleet}
        employees={employees}
        onImported={load}
      />

      {showSalaryForm && (
        <SalaryForm
          employees={employees}
          onClose={() => setShowSalaryForm(false)}
          onSaved={() => {
            setShowSalaryForm(false);
            load();
          }}
        />
      )}
    </div>
  );
}

function TabButton({ id, activeId, setTab, icon: Icon, label, testId, brand, emerald }) {
  const active = activeId === id;
  let activeClass = "bg-zinc-900 text-white";
  if (brand) activeClass = "bg-brand text-white";
  if (emerald) activeClass = "bg-emerald-600 text-white";
  return (
    <button
      onClick={() => setTab(id)}
      data-testid={testId}
      className={`px-5 py-3 text-sm font-bold uppercase tracking-wider border-r border-zinc-900 last:border-r-0 inline-flex items-center gap-2 ${
        active ? activeClass : "bg-white hover:bg-zinc-50"
      }`}
    >
      <Icon className="w-4 h-4" /> {label}
    </button>
  );
}

function CashflowTab({ summary, transactions, vehicleMap, employeeMap }) {
  if (!summary) {
    return <div className="py-12 text-center text-zinc-500">Ładowanie...</div>;
  }
  // Show planned transactions (next 30 days)
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const planned = transactions
    .filter((t) => t.status === "planned" && new Date(t.date) >= today)
    .sort((a, b) => a.date.localeCompare(b.date))
    .slice(0, 8);

  return (
    <div className="space-y-10" data-testid="cashflow-tab">
      <div className="border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
        <strong>ℹ Cashflow tylko do podglądu.</strong> Aby dodać koszt przejdź do
        zakładki <strong>KUP</strong>, aby dodać przychód do zakładki <strong>Przychód</strong>.
      </div>

      <section className="grid grid-cols-2 lg:grid-cols-4 gap-0 border border-zinc-900">
        <Kpi label="Przychód MTD" value={fmtMoney(summary.kpi.mtd_income)} icon={TrendingUp} color="text-emerald-600" testId="kpi-mtd-income" />
        <Kpi label="Koszty MTD" value={fmtMoney(summary.kpi.mtd_expense)} icon={TrendingDown} color="text-red-600" testId="kpi-mtd-expense" dark />
        <Kpi label="Zysk MTD" value={fmtMoney(summary.kpi.mtd_net)} icon={Wallet} color={summary.kpi.mtd_net >= 0 ? "text-emerald-600" : "text-red-600"} testId="kpi-mtd-net" />
        <Kpi label="Projekcja 30 dni" value={fmtMoney(summary.kpi.projected_cashflow_30d)} icon={Calendar} color={summary.kpi.projected_cashflow_30d >= 0 ? "text-emerald-300" : "text-red-300"} testId="kpi-projection" dark />
      </section>

      <section className="border border-zinc-900 p-6 bg-white">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="font-label">// Wykres</div>
            <h2 className="font-heading font-black text-xl uppercase tracking-tight">
              Cashflow · 12 miesięcy historii + 6 projekcji
            </h2>
          </div>
        </div>
        <div style={{ width: "100%", height: 360 }} data-testid="cashflow-chart">
          <ResponsiveContainer>
            <ComposedChart
              data={summary.series.map((s) => ({ ...s, label: fmtMonth(s.month) }))}
              margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#52525b" }} />
              <YAxis tick={{ fontSize: 11, fill: "#52525b" }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip contentStyle={{ borderRadius: 0, border: "1px solid #18181b", background: "#fff" }} formatter={(v, name) => [fmtMoney(v), name]} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <ReferenceLine y={0} stroke="#18181b" strokeWidth={1} />
              <Bar dataKey="income" name="Przychód" fill="#10b981" />
              <Bar dataKey="expense" name="Koszt" fill="#E52C34" />
              <Line type="monotone" dataKey="cashflow" name="Cashflow skumulowany" stroke="#18181b" strokeWidth={2} dot={{ r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </section>

      <section>
        <div className="font-label mb-3">// Planowane transakcje (najbliższe)</div>
        <div className="border border-zinc-900 overflow-x-auto">
          <table className="w-full" data-testid="planned-tx-table">
            <thead>
              <tr className="bg-zinc-900 text-white text-left">
                <Th>Data</Th>
                <Th>Typ</Th>
                <Th>Kategoria</Th>
                <Th>Powiązanie</Th>
                <Th className="text-right">Kwota</Th>
              </tr>
            </thead>
            <tbody>
              {planned.length === 0 && (
                <tr>
                  <td colSpan={5} className="py-10 text-center text-zinc-500 text-sm">
                    Brak zaplanowanych transakcji.
                  </td>
                </tr>
              )}
              {planned.map((t) => (
                <tr key={t.id} className="border-t border-zinc-200 hover:bg-zinc-50">
                  <td className="py-3 px-4 font-mono text-sm">
                    {new Date(t.date).toLocaleDateString("pl-PL")}
                  </td>
                  <td className="py-3 px-4 text-sm">
                    <span className={`inline-block px-2 py-0.5 text-xs font-bold uppercase tracking-wider ${
                      t.type === "income" ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"
                    }`}>
                      {t.type === "income" ? "Przychód" : "Koszt"}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm">{t.category}</td>
                  <td className="py-3 px-4 text-xs text-zinc-600">
                    {t.vehicle_id && vehicleMap[t.vehicle_id] && (
                      <div className="flex items-center gap-1"><Truck className="w-3 h-3" /> {vehicleMap[t.vehicle_id]}</div>
                    )}
                    {t.employee_id && employeeMap[t.employee_id] && (
                      <div className="flex items-center gap-1"><User className="w-3 h-3" /> {employeeMap[t.employee_id]}</div>
                    )}
                    {!t.vehicle_id && !t.employee_id && <span className="text-zinc-400">—</span>}
                  </td>
                  <td className={`py-3 px-4 text-right font-mono font-semibold ${
                    t.type === "income" ? "text-emerald-700" : "text-red-700"
                  }`}>
                    {t.type === "income" ? "+" : "−"} {fmtMoney(t.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function KupTab({ kup, transactions, vehicleMap, employeeMap, onAdd, onImport, onRemove, onEdit }) {
  const [filter, setFilter] = useState({ status: "all" });
  if (!kup) {
    return <div className="py-12 text-center text-zinc-500">Ładowanie...</div>;
  }
  const filtered = transactions.filter((t) =>
    filter.status === "all" ? true : t.status === filter.status
  );

  return (
    <div className="space-y-10" data-testid="kup-tab">
      <div className="flex items-end justify-between">
        <div>
          <div className="font-label">// KUP</div>
          <h2 className="font-heading font-black text-2xl uppercase tracking-tight">
            Koszty Uzyskania Przychodu
          </h2>
        </div>
        <div className="flex gap-2">
          <button
            onClick={onImport}
            data-testid="btn-import-kup"
            className="inline-flex items-center gap-2 border border-zinc-900 text-zinc-900 px-4 py-3 font-bold uppercase tracking-wider text-xs hover:bg-zinc-900 hover:text-white transition-colors"
          >
            <Upload className="w-4 h-4" /> Import z Excel
          </button>
          <button
            onClick={onAdd}
            data-testid="btn-add-kup"
            className="inline-flex items-center gap-2 bg-brand text-white px-5 py-3 font-bold uppercase tracking-wider hover:bg-brand-hover transition-colors"
          >
            <Plus className="w-4 h-4" /> Dodaj koszt
          </button>
        </div>
      </div>

      <section className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-zinc-900">
        <Kpi label="KUP — bieżący miesiąc" value={fmtMoney(kup.total_mtd)} icon={Receipt} color="text-red-600" testId="kup-mtd" />
        <Kpi label="KUP — od początku roku" value={fmtMoney(kup.total_ytd)} icon={Receipt} color="text-red-300" testId="kup-ytd" dark />
      </section>

      <section>
        <div className="font-label mb-3">// Kategorie kosztów (YTD)</div>
        <div className="border border-zinc-900 overflow-x-auto">
          <table className="w-full" data-testid="kup-categories-table">
            <thead>
              <tr className="bg-zinc-900 text-white text-left">
                <Th>Kategoria</Th>
                <Th className="text-right">YTD</Th>
                <Th className="text-right">Udział</Th>
              </tr>
            </thead>
            <tbody>
              {kup.by_category_ytd.length === 0 && (
                <tr><td colSpan={3} className="py-10 text-center text-zinc-500 text-sm">Brak kosztów w bieżącym roku.</td></tr>
              )}
              {kup.by_category_ytd.map((c) => {
                const pct = kup.total_ytd > 0 ? (c.amount / kup.total_ytd) * 100 : 0;
                return (
                  <tr key={c.category} className="border-t border-zinc-200">
                    <td className="py-3 px-4 font-semibold">{c.category}</td>
                    <td className="py-3 px-4 text-right font-mono font-semibold text-red-700">
                      {fmtMoney(c.amount)}
                    </td>
                    <td className="py-3 px-4 text-right text-zinc-600 font-mono text-sm">
                      <div className="flex items-center justify-end gap-2">
                        <div className="w-24 h-2 bg-zinc-200">
                          <div className="h-full bg-brand" style={{ width: `${pct}%` }} />
                        </div>
                        {pct.toFixed(1)}%
                      </div>
                    </td>
                  </tr>
                );
              })}
              {kup.total_ytd > 0 && (
                <tr className="border-t-2 border-zinc-900 bg-zinc-50">
                  <td className="py-3 px-4 font-bold uppercase text-sm">Razem</td>
                  <td className="py-3 px-4 text-right font-mono font-bold text-red-700">
                    {fmtMoney(kup.total_ytd)}
                  </td>
                  <td className="py-3 px-4 text-right font-mono text-sm">100%</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <section>
          <div className="font-label mb-3 flex items-center gap-2"><Truck className="w-3 h-3" /> // Per pojazd (YTD)</div>
          <div className="border border-zinc-900">
            <table className="w-full" data-testid="kup-vehicles-table">
              <thead>
                <tr className="bg-zinc-900 text-white text-left">
                  <Th>Pojazd</Th>
                  <Th className="text-right">YTD</Th>
                </tr>
              </thead>
              <tbody>
                {kup.by_vehicle_ytd.length === 0 && (
                  <tr><td colSpan={2} className="py-8 text-center text-zinc-500 text-sm">Brak kosztów przypisanych do pojazdów.</td></tr>
                )}
                {kup.by_vehicle_ytd.map((v) => (
                  <tr key={v.vehicle_id} className="border-t border-zinc-200">
                    <td className="py-3 px-4 text-sm font-semibold">{v.label}</td>
                    <td className="py-3 px-4 text-right font-mono font-semibold text-red-700">{fmtMoney(v.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
        <section>
          <div className="font-label mb-3 flex items-center gap-2"><User className="w-3 h-3" /> // Per pracownik (YTD)</div>
          <div className="border border-zinc-900">
            <table className="w-full" data-testid="kup-employees-table">
              <thead>
                <tr className="bg-zinc-900 text-white text-left">
                  <Th>Pracownik</Th>
                  <Th className="text-right">YTD</Th>
                </tr>
              </thead>
              <tbody>
                {kup.by_employee_ytd.length === 0 && (
                  <tr><td colSpan={2} className="py-8 text-center text-zinc-500 text-sm">Brak kosztów przypisanych do pracowników.</td></tr>
                )}
                {kup.by_employee_ytd.map((e) => (
                  <tr key={e.employee_id} className="border-t border-zinc-200">
                    <td className="py-3 px-4 text-sm font-semibold">{e.label}</td>
                    <td className="py-3 px-4 text-right font-mono font-semibold text-red-700">{fmtMoney(e.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <section className="space-y-4">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="font-label flex items-center gap-2"><Filter className="w-3 h-3" /> Status</span>
          {[
            { id: "all", label: "Wszystkie" },
            { id: "actual", label: "Zrealizowane" },
            { id: "planned", label: "Planowane" },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter({ status: f.id })}
              data-testid={`kup-filter-${f.id}`}
              className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider border ${
                filter.status === f.id ? "bg-brand text-white border-brand" : "bg-white border-zinc-300 hover:border-zinc-900"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <TxTable
          rows={filtered}
          vehicleMap={vehicleMap}
          employeeMap={employeeMap}
          onRemove={onRemove}
          onEdit={onEdit}
          testId="kup-transactions-table"
        />
      </section>
    </div>
  );
}

function IncomeTab({ summary, transactions, onAdd, onImport, onRemove, onEdit }) {
  const [filter, setFilter] = useState({ status: "all" });
  if (!summary) {
    return <div className="py-12 text-center text-zinc-500">Ładowanie...</div>;
  }
  const filtered = transactions.filter((t) =>
    filter.status === "all" ? true : t.status === filter.status
  );
  return (
    <div className="space-y-10" data-testid="income-tab">
      <div className="flex items-end justify-between">
        <div>
          <div className="font-label">// Przychody</div>
          <h2 className="font-heading font-black text-2xl uppercase tracking-tight">
            Ewidencja przychodów
          </h2>
        </div>
        <div className="flex gap-2">
          <button
            onClick={onImport}
            data-testid="btn-import-income"
            className="inline-flex items-center gap-2 border border-zinc-900 text-zinc-900 px-4 py-3 font-bold uppercase tracking-wider text-xs hover:bg-zinc-900 hover:text-white transition-colors"
          >
            <Upload className="w-4 h-4" /> Import z Excel
          </button>
          <button
            onClick={onAdd}
            data-testid="btn-add-income"
            className="inline-flex items-center gap-2 bg-emerald-600 text-white px-5 py-3 font-bold uppercase tracking-wider hover:bg-emerald-700 transition-colors"
          >
            <Plus className="w-4 h-4" /> Dodaj przychód
          </button>
        </div>
      </div>

      <section className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-zinc-900">
        <Kpi label="Przychód MTD" value={fmtMoney(summary.kpi.mtd_income)} icon={TrendingUp} color="text-emerald-600" testId="income-mtd" />
        <Kpi label="Razem zrealizowane" value={fmtMoney(summary.kpi.total_income_actual)} icon={Wallet} color="text-emerald-600" testId="income-total" dark />
        <Kpi label="Planowane 30 dni" value={fmtMoney(summary.kpi.planned_income_30d)} icon={Calendar} color="text-amber-500" testId="income-planned" />
      </section>

      <section className="space-y-4">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="font-label flex items-center gap-2"><Filter className="w-3 h-3" /> Status</span>
          {[
            { id: "all", label: "Wszystkie" },
            { id: "actual", label: "Zrealizowane" },
            { id: "planned", label: "Planowane" },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter({ status: f.id })}
              data-testid={`income-filter-${f.id}`}
              className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider border ${
                filter.status === f.id ? "bg-emerald-600 text-white border-emerald-600" : "bg-white border-zinc-300 hover:border-zinc-900"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <TxTable rows={filtered} onRemove={onRemove} onEdit={onEdit} testId="income-transactions-table" />
      </section>
    </div>
  );
}

function TxTable({ rows, onRemove, onEdit, vehicleMap, employeeMap, testId }) {
  return (
    <div className="border border-zinc-900 overflow-x-auto">
      <table className="w-full" data-testid={testId}>
        <thead>
          <tr className="bg-zinc-900 text-white text-left">
            <Th>Data</Th>
            <Th>Kategoria</Th>
            {(vehicleMap || employeeMap) && <Th>Powiązanie</Th>}
            <Th>Status</Th>
            <Th className="text-right">Kwota</Th>
            <Th></Th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={6} className="py-12 text-center text-sm text-zinc-500">
                Brak transakcji.
              </td>
            </tr>
          )}
          {rows.map((t) => (
            <tr key={t.id} className="border-t border-zinc-200 hover:bg-zinc-50" data-testid={`tx-row-${t.id}`}>
              <td className="py-3 px-4 font-mono text-sm">
                {new Date(t.date).toLocaleDateString("pl-PL")}
              </td>
              <td className="py-3 px-4 text-sm">
                <div>{t.category}</div>
                {t.description && (
                  <div className="text-xs text-zinc-500 mt-0.5">{t.description}</div>
                )}
              </td>
              {(vehicleMap || employeeMap) && (
                <td className="py-3 px-4 text-xs">
                  {vehicleMap && t.vehicle_id && vehicleMap[t.vehicle_id] && (
                    <div className="flex items-center gap-1 text-zinc-700"><Truck className="w-3 h-3" /> {vehicleMap[t.vehicle_id]}</div>
                  )}
                  {employeeMap && t.employee_id && employeeMap[t.employee_id] && (
                    <div className="flex items-center gap-1 text-zinc-700"><User className="w-3 h-3" /> {employeeMap[t.employee_id]}</div>
                  )}
                  {!t.vehicle_id && !t.employee_id && <span className="text-zinc-400">—</span>}
                </td>
              )}
              <td className="py-3 px-4 text-sm">
                <span className={`inline-block px-2 py-0.5 text-xs font-mono border ${
                  t.status === "actual" ? "border-zinc-300 text-zinc-700" : "border-amber-400 text-amber-700 bg-amber-50"
                }`}>
                  {t.status === "actual" ? "zrealizowane" : "planowane"}
                </span>
              </td>
              <td className={`py-3 px-4 text-right font-mono font-semibold ${
                t.type === "income" ? "text-emerald-700" : "text-red-700"
              }`}>
                {t.type === "income" ? "+" : "−"} {fmtMoney(t.amount)}
              </td>
              <td className="py-3 px-4 text-right">
                <div className="inline-flex items-center gap-1">
                  {onEdit && (
                    <button
                      onClick={() => onEdit(t)}
                      data-testid={`btn-edit-tx-${t.id}`}
                      title="Edytuj"
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-zinc-300 text-zinc-700 hover:border-zinc-900 hover:bg-zinc-900 hover:text-white"
                    >
                      <Pencil className="w-3 h-3" />
                    </button>
                  )}
                  <button
                    onClick={() => onRemove(t.id)}
                    data-testid={`btn-del-tx-${t.id}`}
                    title="Usuń"
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-zinc-300 text-red-600 hover:border-red-600"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SalariesTab({ transactions, employees, employeeMap, onAdd, onRemove }) {
  const [selectedEmpId, setSelectedEmpId] = useState("all");
  const [monthFilter, setMonthFilter] = useState(new Date().toISOString().slice(0, 7));

  // Aggregate per-employee stats for the current month
  const perEmployee = useMemo(() => {
    const map = new Map();
    for (const emp of employees) {
      map.set(emp.id, {
        emp,
        countMonth: 0,
        totalMonth: 0,
        countAll: 0,
        totalAll: 0,
        lastPayment: null,
      });
    }
    // "Unassigned" bucket for salary tx without employee_id
    map.set("__unassigned", {
      emp: { id: "__unassigned", first_name: "Bez", last_name: "przypisania", position: "—", hourly_rate: null },
      countMonth: 0, totalMonth: 0, countAll: 0, totalAll: 0, lastPayment: null,
    });

    for (const t of transactions) {
      const key = t.employee_id || "__unassigned";
      const bucket = map.get(key);
      if (!bucket) continue;
      bucket.countAll += 1;
      bucket.totalAll += t.amount;
      if (t.date && t.date.startsWith(monthFilter)) {
        bucket.countMonth += 1;
        bucket.totalMonth += t.amount;
      }
      if (!bucket.lastPayment || (t.date && t.date > bucket.lastPayment)) {
        bucket.lastPayment = t.date;
      }
    }
    // Return only those with any payment history (skip employees never paid)
    return Array.from(map.values())
      .filter((b) => b.countAll > 0 || b.emp.id !== "__unassigned")
      .sort((a, b) => (b.totalMonth || 0) - (a.totalMonth || 0));
  }, [transactions, employees, monthFilter]);

  const filteredTx = useMemo(() => {
    return transactions
      .filter((t) => {
        if (selectedEmpId === "all") return true;
        if (selectedEmpId === "__unassigned") return !t.employee_id;
        return t.employee_id === selectedEmpId;
      })
      .sort((a, b) => (a.date < b.date ? 1 : -1));
  }, [transactions, selectedEmpId]);

  const monthTotal = perEmployee.reduce((s, b) => s + b.totalMonth, 0);
  const allTimeTotal = transactions.reduce((s, t) => s + t.amount, 0);

  return (
    <div className="space-y-10" data-testid="salaries-tab">
      <div className="flex items-end justify-between">
        <div>
          <div className="font-label">// Wynagrodzenia</div>
          <h2 className="font-heading font-black text-2xl uppercase tracking-tight">
            Wypłaty pracownikom
          </h2>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex flex-col text-xs">
            <span className="font-label mb-1">Miesiąc</span>
            <input
              type="month"
              value={monthFilter}
              onChange={(e) => setMonthFilter(e.target.value)}
              data-testid="salary-month-filter"
              className="border border-zinc-300 bg-white px-3 py-2 font-mono focus:outline-none focus:border-zinc-900"
            />
          </label>
          <button
            onClick={onAdd}
            data-testid="btn-add-salary"
            className="inline-flex items-center gap-2 bg-brand text-white px-5 py-3 font-bold uppercase tracking-wider hover:bg-brand-hover transition-colors"
          >
            <Plus className="w-4 h-4" /> Dodaj wynagrodzenie
          </button>
        </div>
      </div>

      <section className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-zinc-900">
        <Kpi
          label={`Wypłaty ${monthFilter}`}
          value={fmtMoney(monthTotal)}
          icon={User}
          color="text-red-600"
          testId="salary-month-total"
        />
        <Kpi
          label="Suma wszystkie"
          value={fmtMoney(allTimeTotal)}
          icon={Wallet}
          color="text-zinc-900"
          testId="salary-all-total"
          dark
        />
        <Kpi
          label="Pracowników z wypłatami"
          value={perEmployee.filter((b) => b.countAll > 0).length}
          icon={TrendingDown}
          color="text-brand"
          testId="salary-employee-count"
        />
      </section>

      {/* Per-employee cards */}
      <section className="space-y-3">
        <div className="font-label">// Karty pracowników — {monthFilter}</div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {perEmployee.map((b) => {
            const empId = b.emp.id;
            const active = selectedEmpId === empId;
            return (
              <button
                key={empId}
                onClick={() => setSelectedEmpId(active ? "all" : empId)}
                data-testid={`salary-emp-card-${empId}`}
                className={`border-2 p-4 text-left transition-all ${
                  active ? "border-zinc-900 bg-zinc-900 text-white" : "border-zinc-200 hover:border-zinc-900 bg-white"
                }`}
              >
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div>
                    <div className="font-heading font-black text-base uppercase tracking-tight">
                      {b.emp.first_name} {b.emp.last_name}
                    </div>
                    <div className={`text-[11px] font-mono ${active ? "text-white/70" : "text-zinc-500"}`}>
                      {b.emp.position || "—"}
                      {b.emp.hourly_rate ? ` · ${b.emp.hourly_rate.toFixed(2)} zł/h` : ""}
                    </div>
                  </div>
                  <User className={`w-4 h-4 ${active ? "text-white/70" : "text-zinc-400"}`} />
                </div>
                <div className="space-y-1 text-xs">
                  <Row label="Wypłaty w miesiącu" value={b.countMonth} active={active} />
                  <Row label="Suma miesiąca" value={fmtMoney(b.totalMonth)} bold active={active} />
                  <Row label="Wszystkich wypłat" value={b.countAll} active={active} muted />
                  <Row label="Suma łącznie" value={fmtMoney(b.totalAll)} active={active} muted />
                  <Row label="Ostatnia wypłata" value={b.lastPayment || "—"} active={active} muted />
                </div>
              </button>
            );
          })}
          {perEmployee.length === 0 && (
            <div className="col-span-full text-center py-12 text-zinc-400 border border-dashed border-zinc-300">
              Brak wypłat. Dodaj pierwsze wynagrodzenie przyciskiem powyżej.
            </div>
          )}
        </div>
      </section>

      {/* Full salary list */}
      <section className="space-y-4">
        <div className="flex items-center gap-3">
          <div className="font-label">
            // Historia wypłat {selectedEmpId !== "all" && "· filtr aktywny"}
          </div>
          {selectedEmpId !== "all" && (
            <button
              onClick={() => setSelectedEmpId("all")}
              data-testid="salary-clear-filter"
              className="text-xs underline text-zinc-500 hover:text-zinc-900"
            >
              Wyczyść filtr
            </button>
          )}
        </div>
        <table className="w-full text-sm" data-testid="salary-tx-table">
          <thead className="border-b-2 border-zinc-900 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left py-2 font-bold">Data</th>
              <th className="text-left py-2 font-bold">Pracownik</th>
              <th className="text-right py-2 font-bold">Brutto</th>
              <th className="text-left py-2 font-bold pl-4">Opis</th>
              <th className="text-right py-2 font-bold">Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {filteredTx.map((t) => {
              const empName = t.employee_id ? employeeMap[t.employee_id] : null;
              return (
                <tr key={t.id} data-testid={`salary-tx-${t.id}`} className="border-b border-zinc-200 hover:bg-zinc-50">
                  <td className="py-2 font-mono">{t.date}</td>
                  <td className="py-2">{empName || <span className="text-zinc-400 italic">brak przypisania</span>}</td>
                  <td className="py-2 font-mono text-right font-bold">{fmtMoney(t.amount)}</td>
                  <td className="py-2 pl-4 text-zinc-600 max-w-[400px] truncate">{t.description || "—"}</td>
                  <td className="py-2 text-right text-xs uppercase">
                    <span className={`px-2 py-0.5 ${t.status === "actual" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                      {t.status === "actual" ? "Wypłacono" : "Planowane"}
                    </span>
                  </td>
                  <td className="py-2">
                    <button
                      onClick={() => onRemove(t.id)}
                      data-testid={`btn-del-salary-${t.id}`}
                      className="p-1 hover:bg-zinc-200"
                      title="Usuń"
                    >
                      <Trash2 className="w-3 h-3 text-zinc-500" />
                    </button>
                  </td>
                </tr>
              );
            })}
            {filteredTx.length === 0 && (
              <tr>
                <td colSpan={6} className="py-8 text-center text-zinc-400">
                  Brak wpisów{selectedEmpId !== "all" && " dla wybranego pracownika"}.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>
    </div>
  );
}

function Row({ label, value, bold, active, muted }) {
  const activeCls = active ? "text-white/60" : muted ? "text-zinc-400" : "text-zinc-500";
  const valueCls = bold ? "font-bold text-base" : "font-mono";
  return (
    <div className="flex items-center justify-between">
      <span className={`text-[10px] uppercase tracking-wider ${activeCls}`}>{label}</span>
      <span className={valueCls}>{value}</span>
    </div>
  );
}



function TransactionForm({
  form, setForm, submitForm, close, meta, fleet, employees,
  needsVehicle, needsEmployee, editing,
}) {
  const isIncome = form.type === "income";
  const categories = isIncome ? meta.income_categories : meta.expense_categories;

  // Auto-calc: netto ↔ brutto driven by VAT rate.
  // We track which side the user last touched to avoid double conversion loss.
  const onNetChange = (v) => {
    const net = num(v);
    const vat = num(form.vat_rate);
    const gross = round2(net * (1 + vat / 100));
    setForm({
      ...form,
      net_amount: v,
      amount: net > 0 ? String(gross) : "",
      _lastEdited: "net",
    });
  };
  const onGrossChange = (v) => {
    const gross = num(v);
    const vat = num(form.vat_rate);
    const net = round2(vat > 0 ? gross / (1 + vat / 100) : gross);
    setForm({
      ...form,
      amount: v,
      net_amount: gross > 0 ? String(net) : "",
      _lastEdited: "gross",
    });
  };
  const onVatChange = (v) => {
    const vat = num(v);
    if (form._lastEdited === "gross") {
      const gross = num(form.amount);
      const net = round2(vat > 0 ? gross / (1 + vat / 100) : gross);
      setForm({ ...form, vat_rate: v, net_amount: gross > 0 ? String(net) : "" });
    } else {
      const net = num(form.net_amount);
      const gross = round2(net * (1 + vat / 100));
      setForm({ ...form, vat_rate: v, amount: net > 0 ? String(gross) : "" });
    }
  };
  const vatAmount = round2(num(form.amount) - num(form.net_amount));

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-stretch justify-end"
      onClick={close}
      data-testid="tx-form-overlay"
    >
      <form
        onSubmit={submitForm}
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-lg h-full border-l border-zinc-900 p-8 overflow-y-auto"
        data-testid="tx-form"
      >
        <div className="flex items-start justify-between mb-8">
          <div>
            <div className="font-label">// {isIncome ? "Przychód" : "KUP"}</div>
            <h2 className="font-heading font-black text-2xl uppercase tracking-tight">
              {editing
                ? isIncome ? "Edytuj przychód" : "Edytuj koszt"
                : isIncome ? "Dodaj przychód" : "Dodaj koszt"}
            </h2>
          </div>
          <button type="button" onClick={close} className="p-2 hover:bg-zinc-100">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-5">
          <div>
            <span className="font-label block mb-2">Status</span>
            <div className="grid grid-cols-2 gap-0 border border-zinc-900">
              <button
                type="button"
                onClick={() => setForm({ ...form, status: "actual" })}
                data-testid="tx-status-actual"
                className={`py-3 px-4 text-sm font-bold uppercase tracking-wider border-r border-zinc-900 ${
                  form.status === "actual" ? "bg-zinc-900 text-white" : "bg-white hover:bg-zinc-50"
                }`}
              >
                Zrealizowane
              </button>
              <button
                type="button"
                onClick={() => setForm({ ...form, status: "planned" })}
                data-testid="tx-status-planned"
                className={`py-3 px-4 text-sm font-bold uppercase tracking-wider ${
                  form.status === "planned" ? "bg-amber-500 text-zinc-900" : "bg-white hover:bg-zinc-50"
                }`}
              >
                Planowane
              </button>
            </div>
          </div>

          {!isIncome && !editing && (
            <RecurringBlock
              baseDate={form.date}
              enabled={form.recurring}
              months={form.recurring_months}
              onToggle={(v) => setForm({ ...form, recurring: v })}
              onMonthsChange={(months) => setForm({ ...form, recurring_months: months })}
            />
          )}

          <div className="grid grid-cols-2 gap-4">
            <label className="block">
              <span className="font-label block mb-2">
                {isIncome ? "Data sprzedaży" : "Data operacji"}
              </span>
              <input
                type="date"
                data-testid="tx-date"
                className="w-full border border-zinc-300 bg-zinc-50 px-3 py-2 font-mono focus:outline-none focus:border-brand"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                required
              />
            </label>
            <label className="block">
              <span className="font-label block mb-2">Termin płatności</span>
              <input
                type="date"
                data-testid="tx-due-date"
                className="w-full border border-zinc-300 bg-zinc-50 px-3 py-2 font-mono focus:outline-none focus:border-brand"
                value={form.due_date || ""}
                onChange={(e) => setForm({ ...form, due_date: e.target.value })}
              />
            </label>
          </div>

          <div className="border border-zinc-300 bg-zinc-50 p-4 space-y-3" data-testid="tx-amounts">
            <div className="font-label flex items-center justify-between">
              <span>Kwoty · auto-kalkulacja VAT</span>
              <span className="text-[10px] font-mono text-zinc-500">
                {form._lastEdited === "gross" ? "brutto → netto" : "netto → brutto"}
              </span>
            </div>
            <div className="grid grid-cols-[1fr_auto_1fr] gap-3 items-end">
              <label className="block">
                <span className="text-[10px] font-mono uppercase text-zinc-600 block mb-1">Netto (zł)</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  data-testid="tx-net-amount"
                  className="w-full border border-zinc-300 bg-white px-3 py-2 font-mono focus:outline-none focus:border-brand"
                  value={form.net_amount}
                  onChange={(e) => onNetChange(e.target.value)}
                  placeholder="0.00"
                />
              </label>
              <label className="block">
                <span className="text-[10px] font-mono uppercase text-zinc-600 block mb-1">VAT</span>
                <select
                  data-testid="tx-vat-rate"
                  className="border border-zinc-300 bg-white px-3 py-2 font-mono focus:outline-none focus:border-brand"
                  value={form.vat_rate}
                  onChange={(e) => onVatChange(e.target.value)}
                >
                  {VAT_RATES.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="text-[10px] font-mono uppercase text-zinc-600 block mb-1">Brutto (zł)</span>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  data-testid="tx-amount"
                  className="w-full border border-zinc-300 bg-white px-3 py-2 font-mono focus:outline-none focus:border-brand"
                  value={form.amount}
                  onChange={(e) => onGrossChange(e.target.value)}
                  required
                />
              </label>
            </div>
            {num(form.amount) > 0 && (
              <div
                className="flex items-center justify-end gap-2 text-[11px] font-mono text-zinc-600"
                data-testid="tx-vat-summary"
              >
                VAT: <span className="font-bold text-zinc-900">
                  {vatAmount.toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} zł
                </span>
              </div>
            )}
          </div>

          <label className="block">
            <span className="font-label block mb-2">Kategoria</span>
            <select
              data-testid="tx-category"
              className="w-full border border-zinc-300 bg-zinc-50 px-3 py-2 focus:outline-none focus:border-brand"
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value, vehicle_id: "", employee_id: "" })}
              required
            >
              <option value="">— wybierz —</option>
              {categories.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </label>

          {needsVehicle && (
            <label className="block" data-testid="tx-vehicle-picker-wrap">
              <span className="font-label block mb-2 flex items-center gap-2">
                <Truck className="w-3 h-3" /> Pojazd
              </span>
              <select
                data-testid="tx-vehicle"
                className="w-full border border-zinc-300 bg-zinc-50 px-3 py-2 focus:outline-none focus:border-brand"
                value={form.vehicle_id}
                onChange={(e) => setForm({ ...form, vehicle_id: e.target.value })}
              >
                <option value="">— nie przypisuj —</option>
                {fleet.map((v) => (
                  <option key={v.id} value={v.id}>{v.brand} {v.model} · {v.registration}</option>
                ))}
              </select>
            </label>
          )}

          {needsEmployee && (
            <label className="block" data-testid="tx-employee-picker-wrap">
              <span className="font-label block mb-2 flex items-center gap-2">
                <User className="w-3 h-3" /> Pracownik
              </span>
              <select
                data-testid="tx-employee"
                className="w-full border border-zinc-300 bg-zinc-50 px-3 py-2 focus:outline-none focus:border-brand"
                value={form.employee_id}
                onChange={(e) => setForm({ ...form, employee_id: e.target.value })}
              >
                <option value="">— nie przypisuj —</option>
                {employees.map((emp) => (
                  <option key={emp.id} value={emp.id}>{emp.first_name} {emp.last_name}</option>
                ))}
              </select>
            </label>
          )}

          <label className="block">
            <span className="font-label block mb-2">Opis</span>
            <textarea
              data-testid="tx-description"
              rows={3}
              className="w-full border border-zinc-300 bg-zinc-50 px-3 py-2 focus:outline-none focus:border-brand"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="np. faktura nr 123/2026"
            />
          </label>
        </div>

        <div className="mt-10 flex gap-3">
          <button
            type="submit"
            data-testid="btn-submit-tx"
            className={`flex-1 inline-flex items-center justify-center gap-2 text-white px-6 py-3 font-bold uppercase tracking-wider transition-colors ${
              isIncome ? "bg-emerald-600 hover:bg-emerald-700" : "bg-brand hover:bg-brand-hover"
            }`}
          >
            {editing ? "Zapisz zmiany" : "Zapisz"}
          </button>
          <button
            type="button"
            onClick={close}
            className="px-6 py-3 font-bold uppercase tracking-wider border border-zinc-300 hover:border-zinc-900"
          >
            Anuluj
          </button>
        </div>
      </form>
    </div>
  );
}

function Kpi({ label, value, icon: Icon, color, testId, dark }) {
  return (
    <div
      data-testid={testId}
      className={`p-6 border-r border-zinc-900 last:border-r-0 ${
        dark ? "bg-zinc-900 text-white" : "bg-white text-zinc-900"
      }`}
    >
      <div className="flex items-start justify-between">
        <div className={`font-label ${dark ? "text-zinc-400" : "text-zinc-500"}`}>
          {label}
        </div>
        <Icon className={`w-4 h-4 ${color || "opacity-60"}`} />
      </div>
      <div className={`mt-6 font-heading font-black text-3xl ${color || ""}`}>
        {value}
      </div>
    </div>
  );
}

function Th({ children, className = "" }) {
  return (
    <th className={`py-3 px-4 text-xs uppercase tracking-[0.2em] font-bold ${className}`}>
      {children}
    </th>
  );
}

function RecurringBlock({ baseDate, enabled, months, onToggle, onMonthsChange }) {
  const baseYm = (baseDate || "").slice(0, 7);
  const [year, monthIdx] = baseYm
    ? [parseInt(baseYm.slice(0, 4), 10), parseInt(baseYm.slice(5, 7), 10) - 1]
    : [new Date().getFullYear(), new Date().getMonth()];
  const allYm = Array.from({ length: 12 }, (_, i) =>
    `${year}-${String(i + 1).padStart(2, "0")}`
  );
  const selected = new Set(months);
  selected.add(baseYm);

  const toggle = (ym) => {
    if (ym === baseYm) return;
    const next = new Set(months);
    if (next.has(ym)) next.delete(ym);
    else next.add(ym);
    onMonthsChange(Array.from(next).sort());
  };
  const setYear = () => onMonthsChange(allYm.filter((ym) => ym !== baseYm));
  const setFutureOnly = () => onMonthsChange(allYm.filter((_, i) => i > monthIdx));
  const clear = () => onMonthsChange([]);

  const extraCount = months.filter((ym) => ym !== baseYm).length;

  return (
    <div className="border border-zinc-300 bg-zinc-50 p-4 space-y-3" data-testid="recurring-block">
      <label className="inline-flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => onToggle(e.target.checked)}
          data-testid="recurring-toggle"
          className="w-4 h-4 accent-brand"
        />
        <span className="font-label">// Koszt cykliczny — powtórz w innych miesiącach</span>
      </label>
      {enabled && (
        <>
          <div className="text-xs text-zinc-600">
            Zaznacz miesiące roku <span className="font-mono font-bold">{year}</span>, w których ma zostać utworzona kopia tego kosztu (ten sam dzień miesiąca, ta sama kwota, kategoria, opis).
          </div>
          <div className="grid grid-cols-6 gap-1.5" data-testid="recurring-months-grid">
            {allYm.map((ym, i) => {
              const isPrimary = ym === baseYm;
              const isSelected = selected.has(ym);
              return (
                <button
                  key={ym}
                  type="button"
                  disabled={isPrimary}
                  onClick={() => toggle(ym)}
                  data-testid={`recurring-month-${ym}`}
                  className={`py-2 text-xs font-bold uppercase tracking-wider border transition-colors ${
                    isPrimary
                      ? "border-zinc-900 bg-zinc-900 text-white cursor-not-allowed"
                      : isSelected
                      ? "border-brand bg-brand text-white hover:bg-brand-hover"
                      : "border-zinc-300 bg-white hover:border-zinc-900"
                  }`}
                  title={isPrimary ? "Miesiąc główny (zawsze dodany)" : `Miesiąc: ${ym}`}
                >
                  {PL_MONTHS[i]}
                </button>
              );
            })}
          </div>
          <div className="flex items-center justify-between gap-2 pt-1">
            <div className="text-xs font-mono text-zinc-600">
              Utworzonych zostanie: <span className="font-bold">{extraCount + 1}</span> wpisów
              {extraCount > 0 && (
                <span className="text-zinc-400"> (1 główny + {extraCount} cyklicznych)</span>
              )}
            </div>
            <div className="flex gap-1.5 text-[10px]">
              <button type="button" onClick={setFutureOnly} data-testid="recurring-future" className="px-2 py-1 border border-zinc-300 hover:border-zinc-900 font-bold uppercase tracking-wider">Do końca roku</button>
              <button type="button" onClick={setYear} data-testid="recurring-all" className="px-2 py-1 border border-zinc-300 hover:border-zinc-900 font-bold uppercase tracking-wider">Cały rok</button>
              <button type="button" onClick={clear} data-testid="recurring-clear" className="px-2 py-1 border border-zinc-300 hover:border-zinc-900 font-bold uppercase tracking-wider">Wyczyść</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

